import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Breadcrumbs, Button, EditButton, FormWrapper, SubmitButton, Table, UserDetails } from "../components";
import { useCompany, useProject } from "../hooks";

const ProjectOwnersPage = () => {
  const tableColumnHeaders = [
    { id: "company_name", name: "Company Name" },
    { id: "project_name", name: "Project Name" },
    { id: "total_commission", name: "Total Commission" },
    { id: "total_payable_now", name: "Total Payable Now" },
    { id: "total_paid", name: "Total Paid" },
    { id: "total_payable_in_future_installments", name: "Total Payable in Future Installments" },
    { id: "action", name: "More Details" },
  ];

  const [projectOwners, setProjectOwners] = useState([]);
  const [searchParams] = useSearchParams();
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [ownerBankDetails, setOwnerBankDetails] = useState([]);
  const [ownerDetails, setOwnerDetails] = useState({});
  const [firmDetails, setFirmDetails] = useState({});
  const [projectDetails, setProjectDetails] = useState({});

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) setSelectedProjectId(id);
  }, [searchParams]);

  const { fetchAllProjectOwnersWithCommission, fetchProjectOwnerById } = useProject();

  useEffect(() => {
    const fetchProjectOwners = async () => {
      const { owners, error } = await fetchAllProjectOwnersWithCommission();
      if (error) return toast.error(error);
      setProjectOwners(owners);
    };
    fetchProjectOwners();
  }, []);

  const fetchProjectOwner = async () => {
    const { owner, error } = await fetchProjectOwnerById(selectedProjectId);
    if (error) return toast.error(error);
    setOwnerDetails(owner.owner);
    setOwnerBankDetails(owner.banks);
    setProjectDetails(owner.project);
    setFirmDetails(owner.company);
  };
  useEffect(() => {
    if (selectedProjectId) fetchProjectOwner();
  }, [selectedProjectId]);

  const [firmEditable, setFirmEditable] = useState(false);
  const [firmFormData, setFirmFormData] = useState({});
  const [editedFirmData, setEditedFirmData] = useState({});
  const { updateCompany } = useCompany();
  const handleUpdateCompany = async (data) => {
    const { success, error } = await updateCompany(firmDetails.company_id, data);
    if (error) return toast.error(error);
    toast.success(success);
    await fetchProjectOwner();
  };
  const handleImageUpload = (id, url, key) => {
    if (key === "aadhar") return handleUpdateCompany({ owner: { owner_aadhar: id } });
    if (key === "pan") return handleUpdateCompany({ owner: { owner_pan: id } });
  };

  const handleEditOwnerDetails = (data) => {
    let dataToEdit = data;
    if (dataToEdit.contact) {
      dataToEdit.mobile = dataToEdit.contact;
    }
    handleUpdateCompany({ owner: dataToEdit });
  };

  const handleSaveFirmDetails = (e) => {
    e.preventDefault();
    setFirmEditable(!firmEditable);
    handleUpdateCompany(editedFirmData);
  };

  const handleCancelFirmEdit = () => {
    setFirmEditable(false);
    setEditedFirmData({});
    setFirmFormData({});
  };

  useEffect(() => {
    if (firmEditable) {
      setFirmFormData(firmDetails);
    }
  }, [firmEditable]);

  const detailsRef = useRef();
  useEffect(() => {
    if (selectedProjectId && detailsRef.current)
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedProjectId]);

  return (
    <>
      <Breadcrumbs />
      <Table
        columns={tableColumnHeaders}
        data={projectOwners}
        title="Project Owners"
        setSelectedId={setSelectedProjectId}
      />
      {selectedProjectId && ownerDetails && ownerBankDetails && projectDetails && firmDetails && (
        <main className="section_wrapper" ref={detailsRef}>
          <UserDetails
            parentName="Owner"
            title={`${firmDetails.company_name} (Owner Profile)`}
            showEditButton
            handleEdit={handleEditOwnerDetails}
            handleAadharUpload={(id, url) => handleImageUpload(id, url, "aadhar")}
            handlePanUpload={(id, url) => handleImageUpload(id, url, "pan")}
            data={ownerDetails}
          />

          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title">Firm Profile</h2>
              {!firmEditable && <EditButton onClick={() => setFirmEditable(true)} />}
            </section>
            <FormWrapper id="firm_details_form" onSubmit={handleSaveFirmDetails}>
              <section className="column">
                {firmEditable ? (
                  <div className="row">
                    <label htmlFor="company_name" className="label gray">
                      Firm Name
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      id="company_name"
                      required
                      value={firmFormData.company_name}
                      onChange={(e) => {
                        setEditedFirmData((prevData) => ({
                          ...prevData,
                          name: e.target.value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1)),
                        }));
                        setFirmFormData((prevData) => ({
                          ...prevData,
                          company_name: e.target.value.replace(
                            /\w\S*/g,
                            (txt) => txt.charAt(0).toUpperCase() + txt.slice(1)
                          ),
                        }));
                      }}
                      className="input input_round"
                      pattern="([a-zA-Z\s+]+)"
                      title="Only alphabets are allowed"
                      maxLength={50}
                    />
                  </div>
                ) : (
                  <div className="row">
                    <span className="label">Firm Name</span>
                    <span className="value">{firmDetails.company_name}</span>
                  </div>
                )}
              </section>
              {firmEditable && (
                <div className="row end">
                  <Button className="ghost_gray" onClick={handleCancelFirmEdit}>
                    Cancel
                  </Button>
                  <SubmitButton form="firm_details_form" className="submit">
                    Save
                  </SubmitButton>
                </div>
              )}
            </FormWrapper>
          </section>
        </main>
      )}
    </>
  );
};
export default ProjectOwnersPage;
